// ISDP CUSTOM: System (Resource Monitor) — admin section.
// A "how stressed are we" dashboard for the running container app (the Node
// process = the container's workload) and the Postgres DB. It fetches
// /api/resource-monitor/metrics (admin-gated, aggregate numbers only — no
// secrets), renders gauge/meter tiles, and auto-refreshes every ~10s.
//
// This page is the admin area's "System" section, so it adopts the SHARED admin
// visual system (AdminHero + the .devhub-admin* classes from '@internal/plugin-admin').
// Importing AdminHero pulls in AdminShell.css. The radial gauge SVGs and the
// health-colour logic (healthFor / cacheHitHealth) stay bespoke; their captions,
// labels and containers are aligned to the shared system so they read the same
// as the other admin sections. The UI is admin-gated: if the API returns 403 we
// render an access-denied panel instead of the dashboard.
import { useCallback, useEffect, useRef, useState } from 'react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { AdminHero, CatalogStatusCard } from '@internal/plugin-admin';
import './ResourceMonitorPage.css';

const REFRESH_MS = 10_000;

type Metrics = {
  generatedAt: string;
  process: {
    rssMB: number;
    heapUsedMB: number;
    heapTotalMB: number;
    heapLimitMB: number;
    memory: {
      usedMB: number;
      limitMB: number;
      usedPct: number;
      source: 'container' | 'heap';
    };
    cpuUserPct: number;
    uptimeSec: number;
    eventLoopLagMs: number;
  };
  os: {
    loadAvg1: number;
    loadAvg5: number;
    loadAvg15: number;
    totalMemMB: number;
    freeMemMB: number;
    memUsedPct: number;
    cpuCount: number;
  };
  db:
    | {
        client: string;
        available: true;
        activeConnections: number;
        idleConnections: number;
        maxConnections: number;
        dbSizeMB: number;
        cacheHitRatioPct: number;
        longestQuerySec: number;
      }
    | { client: string; available: false };
};

// Tri-state health colour by threshold. warn/crit are the "getting stressed" /
// "stressed" cut points; below warn is healthy.
type Health = 'ok' | 'warn' | 'crit';
const healthFor = (v: number, warn: number, crit: number): Health => {
  if (v >= crit) return 'crit';
  if (v >= warn) return 'warn';
  return 'ok';
};

// Cache hit ratio is "higher is better" (inverse of healthFor's thresholds). 95%+ is a
// healthy Postgres buffer-cache ratio, so that's green; only a genuinely low ratio warns.
const cacheHitHealth = (pct: number): Health => {
  if (pct >= 95) return 'ok';
  if (pct >= 85) return 'warn';
  return 'crit';
};

function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatMB(mb: number): string {
  return mb >= 1024
    ? `${(mb / 1024).toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })} GB`
    : `${mb.toLocaleString(undefined, { maximumFractionDigits: 0 })} MB`;
}

// A radial gauge (0–100%) used for the percentage tiles. Bespoke SVG — kept as-is;
// only its container/caption below adopt the shared admin classes.
function Gauge({
  pct,
  health,
  centerLabel,
}: {
  pct: number;
  health: Health;
  centerLabel: string;
}) {
  const clamped = Math.min(100, Math.max(0, pct));
  const R = 52;
  const C = 2 * Math.PI * R;
  const dash = (clamped / 100) * C;
  return (
    <svg
      className="devhub-rm-gauge"
      viewBox="0 0 130 130"
      role="img"
      aria-label={`${centerLabel}: ${clamped}%`}
    >
      <circle className="devhub-rm-gauge__track" cx="65" cy="65" r={R} />
      <circle
        className={`devhub-rm-gauge__fill devhub-rm-gauge__fill--${health}`}
        cx="65"
        cy="65"
        r={R}
        strokeDasharray={`${dash} ${C - dash}`}
        transform="rotate(-90 65 65)"
      />
      <text className="devhub-rm-gauge__value" x="65" y="62">
        {Math.round(clamped)}%
      </text>
      <text className="devhub-rm-gauge__label" x="65" y="82">
        {centerLabel}
      </text>
    </svg>
  );
}

// A KPI tile with a horizontal meter + absolute value + optional sub-line.
// Uses the shared .devhub-adminstat surface, .devhub-admin-t-* type and the
// shared .devhub-adminmeter track/fill.
function Meter({
  title,
  icon,
  pct,
  health,
  primary,
  sub,
}: {
  title: string;
  icon: string;
  pct: number;
  health: Health;
  primary: string;
  sub?: string;
}) {
  return (
    <div className="devhub-adminstat">
      <div className="devhub-rm-tilehead">
        <odx-icon name={icon} size="sm" />
        <span className="devhub-admin-t-statlabel">{title}</span>
      </div>
      <div className="devhub-admin-t-statvalue">{primary}</div>
      <div className="devhub-adminmeter__track">
        <div
          className={`devhub-adminmeter__fill devhub-adminmeter__fill--${health}`}
          style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
        />
      </div>
      {sub ? <div className="devhub-admin-t-caption">{sub}</div> : null}
    </div>
  );
}

// A plain KPI tile (no meter) for scalar readouts.
function Stat({
  title,
  icon,
  value,
  sub,
  health,
}: {
  title: string;
  icon: string;
  value: string;
  sub?: string;
  health?: Health;
}) {
  return (
    <div className="devhub-adminstat">
      <div className="devhub-rm-tilehead">
        <odx-icon name={icon} size="sm" />
        <span className="devhub-admin-t-statlabel">{title}</span>
      </div>
      <div
        className={`devhub-admin-t-statvalue${
          health ? ` devhub-rm-statvalue--${health}` : ''
        }`}
      >
        {value}
      </div>
      {sub ? <div className="devhub-admin-t-caption">{sub}</div> : null}
    </div>
  );
}

// Section heading (icon + title) shared by the Container app / Database blocks.
function SectionHead({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="devhub-rm-sechead">
      <odx-icon name={icon} size="sm" />
      <h2 className="devhub-admin-t-section">{title}</h2>
    </div>
  );
}

export const ResourceMonitorPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const configApi = useApi(configApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [data, setData] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await fetchApi.fetch(
        `${backendUrl}/api/resource-monitor/metrics`,
      );
      if (resp.status === 403) {
        setDenied(true);
        setError(null);
        if (timer.current) clearInterval(timer.current);
        return;
      }
      if (!resp.ok) throw new Error(`Request failed (${resp.status})`);
      setData(await resp.json());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, fetchApi]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, REFRESH_MS);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  if (denied) {
    return (
      <>
        <AdminHero
          title="System"
          eyebrow="Admin"
          intro="Container & database health at a glance."
        />
        <div className="devhub-adminsec">
          <div className="devhub-rm-state devhub-rm-state--error">
            <odx-icon name="core::lock" size="sm" /> Access denied — this page is
            restricted to administrators.
          </div>
        </div>
      </>
    );
  }

  const p = data?.process;
  const o = data?.os;
  const db = data?.db;

  const dbConnPct =
    db && db.available && db.maxConnections > 0
      ? ((db.activeConnections + db.idleConnections) / db.maxConnections) * 100
      : 0;

  return (
    <>
      <AdminHero
        title="System"
        eyebrow="Admin"
        intro="Container & database health at a glance."
      />
      <div className="devhub-adminsec">
        {data && (
          <div className="devhub-rm-updated">
            <span className="devhub-rm-live-dot" /> Live health of the container
            app and database — auto-refreshes every 10 seconds. Updated{' '}
            {new Date(data.generatedAt).toLocaleTimeString()}
          </div>
        )}

        {/* Catalog Status — salvaged from the removed admin Overview page. */}
        <div className="devhub-adminsec__block">
          <CatalogStatusCard />
        </div>

        {loading && !data && (
          <div className="devhub-adminsec__block">
            <div className="devhub-rm-state">Loading metrics…</div>
          </div>
        )}
        {error && (
          <div className="devhub-adminsec__block">
            <div className="devhub-rm-state devhub-rm-state--error">{error}</div>
          </div>
        )}

        {data && p && o && (
          <>
            {/* Gauges — the headline "how stressed are we" signals */}
            <div className="devhub-adminsec__block">
              <div className="devhub-adminsec__grid devhub-adminsec__grid--4">
                <div className="devhub-rm-gauge-card">
                  <Gauge
                    pct={p.cpuUserPct}
                    health={healthFor(p.cpuUserPct, 60, 85)}
                    centerLabel="CPU"
                  />
                  <span className="devhub-admin-t-caption">
                    Process CPU (user)
                  </span>
                </div>
                <div className="devhub-rm-gauge-card">
                  <Gauge
                    pct={p.memory.usedPct}
                    health={healthFor(p.memory.usedPct, 75, 90)}
                    centerLabel="Memory"
                  />
                  <span className="devhub-admin-t-caption">
                    {p.memory.source === 'container'
                      ? 'Container memory used'
                      : 'Heap used (of V8 limit)'}
                  </span>
                </div>
                {db && db.available && (
                  <div className="devhub-rm-gauge-card">
                    <Gauge
                      pct={dbConnPct}
                      health={healthFor(dbConnPct, 70, 90)}
                      centerLabel="DB conns"
                    />
                    <span className="devhub-admin-t-caption">
                      Connections used
                    </span>
                  </div>
                )}
                {db && db.available && (
                  <div className="devhub-rm-gauge-card">
                    <Gauge
                      pct={db.cacheHitRatioPct}
                      health={cacheHitHealth(db.cacheHitRatioPct)}
                      centerLabel="Cache hit"
                    />
                    <span className="devhub-admin-t-caption">
                      DB cache-hit ratio
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Container app (Node process + host) */}
            <div className="devhub-adminsec__block">
              <SectionHead icon="core::service" title="Container app" />
              <div className="devhub-adminsec__grid">
                <Meter
                  title="Heap used"
                  icon="core::bar-chart"
                  pct={
                    p.heapLimitMB > 0 ? (p.heapUsedMB / p.heapLimitMB) * 100 : 0
                  }
                  health={healthFor(
                    p.heapLimitMB > 0 ? (p.heapUsedMB / p.heapLimitMB) * 100 : 0,
                    75,
                    90,
                  )}
                  primary={`${formatMB(p.heapUsedMB)} / ${formatMB(
                    p.heapLimitMB,
                  )}`}
                  sub={`Allocated ${formatMB(p.heapTotalMB)} · RSS ${formatMB(
                    p.rssMB,
                  )}`}
                />
                <Meter
                  title="Event-loop lag"
                  icon="core::time"
                  pct={Math.min(100, (p.eventLoopLagMs / 100) * 100)}
                  health={healthFor(p.eventLoopLagMs, 50, 100)}
                  primary={`${p.eventLoopLagMs.toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })} ms`}
                  sub="Mean scheduling delay"
                />
                <Stat
                  title="Uptime"
                  icon="core::reload"
                  value={formatUptime(p.uptimeSec)}
                  sub="Since process start"
                />
                <Stat
                  title="Load average"
                  icon="core::dashboard"
                  value={`${o.loadAvg1} / ${o.loadAvg5} / ${o.loadAvg15}`}
                  sub={`1 / 5 / 15 min · ${o.cpuCount} CPU${
                    o.cpuCount === 1 ? '' : 's'
                  }`}
                  health={healthFor(
                    o.cpuCount > 0 ? (o.loadAvg1 / o.cpuCount) * 100 : 0,
                    80,
                    120,
                  )}
                />
                {/* Informational only (no health colour): host memory includes OS cache and,
                    inside a container, reflects the host VM — not the app's own pressure, which
                    the Memory gauge above already tracks against the real limit. */}
                <Stat
                  title="Host memory"
                  icon="core::aggregation"
                  value={`${formatMB(o.totalMemMB - o.freeMemMB)} / ${formatMB(
                    o.totalMemMB,
                  )}`}
                  sub={`${formatMB(o.freeMemMB)} free · incl. OS cache`}
                />
              </div>
            </div>

            {/* Database */}
            <div className="devhub-adminsec__block">
              <SectionHead icon="core::aggregation" title="Database" />
              {db && db.available ? (
                <div className="devhub-adminsec__grid">
                  <Meter
                    title="Connections"
                    icon="core::link"
                    pct={dbConnPct}
                    health={healthFor(dbConnPct, 70, 90)}
                    primary={`${db.activeConnections + db.idleConnections} / ${
                      db.maxConnections
                    }`}
                    sub={`${db.activeConnections} active · ${db.idleConnections} idle`}
                  />
                  <Stat
                    title="Database size"
                    icon="core::object"
                    value={formatMB(db.dbSizeMB)}
                    sub={db.client}
                  />
                  <Stat
                    title="Cache-hit ratio"
                    icon="core::check"
                    value={`${db.cacheHitRatioPct.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}%`}
                    sub="Buffer cache efficiency"
                    health={cacheHitHealth(db.cacheHitRatioPct)}
                  />
                  <Stat
                    title="Longest running query"
                    icon="core::time"
                    value={`${db.longestQuerySec.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })} s`}
                    sub="Active statements"
                    health={healthFor(db.longestQuerySec, 5, 30)}
                  />
                </div>
              ) : (
                <div className="devhub-rm-state">
                  Database metrics are unavailable for the{' '}
                  <strong>{db?.client ?? 'current'}</strong> client (local
                  development uses SQLite). Detailed DB stats are shown when
                  running against Postgres.
                </div>
              )}
            </div>

            <p className="devhub-admin-t-caption devhub-rm-note">
              Aggregate metrics only — no configuration, credentials, or
              connection details are exposed. Values are cached for ~10s.
            </p>
          </>
        )}
      </div>
    </>
  );
};
